/**
 * OnboardingApprovalsPage — Founder review gate for self-service subscribers
 *
 * Shows companies with subscription state === 'onboarding' that have
 * uploaded and processed data (onboarding.dataProcessed === true).
 *
 * Files are grouped by entity code (extracted from filenames or upload_intent metadata).
 * Each entity group can be mapped to an office + template before processing.
 * Founder clicks "Approve & Activate" to transition ONBOARDING → ACTIVE.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { extractEntityCode } from '@valutten/shared-utils';
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  doc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import {
  CheckCircle,
  Loader2,
  RefreshCw,
  Building2,
  FileText,
  AlertTriangle,
  Clock,
  Download,
  Mail,
  Play,
  Tag,
  Plus,
  Layers,
  Trash2,
  ArrowRight,
  CheckSquare,
  Square,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Search,
  ArrowUpDown,
  CopyMinus,
} from 'lucide-react';
import { Toast, type ToastType } from '../components/common/Toast';
import { XCircle, ShieldAlert } from 'lucide-react';
import type { OnboardingReadinessResult } from '../types/onboardingReadiness';

interface PendingCompany {
  id: string;
  name: string;
  aclNumber?: string;
  abn?: string;
  createdAt?: any;
  subscriptionTier?: string;
  subscriptionAmount?: number;
  dataProcessed: boolean;
  dataProcessedAt?: any;
  uploadedFiles: UploadedFile[];
  commissionEmails?: string[];
  otherAggregatorName?: string;
  enabledTemplateIds?: string[];
  aggregatorPreferences?: string[];
  hasStagedFiles: boolean;
  contactEmail?: string;
  contactName?: string;
}

interface FileTemplate {
  id: string;
  templateName: string;
}

interface CompanyOffice {
  id: string;
  name: string;
  isHeadOffice?: boolean;
}

interface UploadedFile {
  id: string;
  fileName: string;
  originalFileName: string;
  displayName: string;
  status: string;
  storagePath?: string;
  createdAt?: any;
  entityCode?: string | null;
  subscriberLoanBookName?: string | null;
  subscriberOfficeId?: string | null;
  subscriberOfficeName?: string | null;
  loanBookGroupKey?: string | null;
}

interface EntityGroup {
  entityCode: string;
  loanBookGroupKey: string;
  files: UploadedFile[];
  subscriberName: string | null;
  subscriberOfficeId: string | null;
  subscriberOfficeName: string | null;
  isMapped: boolean;
  mappedOfficeName?: string;
  mappedTemplateId?: string;
  mappedOfficeId?: string;
}

interface EntityMappingLocal {
  entityCode: string;
  officeId: string;
  officeName: string;
  templateId: string;
}

/**
 * Extract entity code from filename prefix. Browser-safe version.
 */
function extractEntityCodeFromFilename(filename: string): string | null {
  // Delegates to the one shared implementation. The inline copy here never
  // received the 2026-08-28 dot fix, so a dotted anchor such as
  // `MAVI1.00 AGMT ...` showed as ungrouped on the approvals screen.
  return extractEntityCode(filename);
}

/**
 * Group uploaded files by entity code.
 */
function groupFilesByEntityCode(files: UploadedFile[]): EntityGroup[] {
  const groups = new Map<string, UploadedFile[]>();

  for (const file of files) {
    // Use loanBookGroupKey from subscriber wizard, fall back to entity code extraction
    const key =
      file.loanBookGroupKey ||
      file.entityCode ||
      extractEntityCodeFromFilename(file.originalFileName || file.fileName || '') ||
      'Unmatched';
    const existing = groups.get(key) || [];
    existing.push(file);
    groups.set(key, existing);
  }

  const result: EntityGroup[] = [];
  const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
    if (a === 'Unmatched') return 1;
    if (b === 'Unmatched') return -1;
    return a.localeCompare(b);
  });

  for (const key of sortedKeys) {
    const groupFiles = groups.get(key)!;
    const subscriberName =
      groupFiles.find((f) => f.subscriberLoanBookName)?.subscriberLoanBookName || null;
    const subscriberOfficeId =
      groupFiles.find((f) => f.subscriberOfficeId)?.subscriberOfficeId || null;
    const subscriberOfficeName =
      groupFiles.find((f) => f.subscriberOfficeName)?.subscriberOfficeName || null;

    result.push({
      entityCode: key,
      loanBookGroupKey: key,
      files: groupFiles,
      subscriberName,
      subscriberOfficeId,
      subscriberOfficeName,
      isMapped: false,
    });
  }

  return result;
}

const DELETABLE_FILE_STATUSES = ['staged', 'pending', 'queued'];

/**
 * Sort files by the given sort key
 */
function applySortToFiles(
  files: UploadedFile[],
  sortKey: 'name-asc' | 'name-desc' | 'date-desc'
): UploadedFile[] {
  return [...files].sort((a, b) => {
    if (sortKey === 'name-asc') {
      return (a.originalFileName || a.displayName || '').localeCompare(
        b.originalFileName || b.displayName || ''
      );
    }
    if (sortKey === 'name-desc') {
      return (b.originalFileName || b.displayName || '').localeCompare(
        a.originalFileName || a.displayName || ''
      );
    }
    // date-desc (default)
    const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
    const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
    return bTime - aTime;
  });
}

/**
 * Detect duplicate filenames within a list of files
 */
function detectDuplicates(files: UploadedFile[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const f of files) {
    const name = f.originalFileName || f.fileName;
    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  }
  return duplicates;
}

/**
 * Count how many duplicate copies exist (for "Select N duplicates" button)
 */
function countDuplicateCopies(files: UploadedFile[], duplicateNames: Set<string>): number {
  const byName = new Map<string, number>();
  for (const f of files) {
    if (!DELETABLE_FILE_STATUSES.includes(f.status)) continue;
    const name = f.originalFileName || f.fileName;
    if (duplicateNames.has(name)) {
      byName.set(name, (byName.get(name) || 0) + 1);
    }
  }
  // For each duplicate group, count = total - 1 (keep one)
  let count = 0;
  byName.forEach((n) => {
    if (n > 1) count += n - 1;
  });
  return count;
}

const OnboardingApprovalsPage: React.FC = () => {
  const [companies, setCompanies] = useState<PendingCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [approving, setApproving] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const processingRef = useRef<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Readiness gate modal — opens when verifyOnboardingReady reports failures.
  // Founder either cancels OR enters a forceActivationReason and overrides.
  const [readinessGate, setReadinessGate] = useState<{
    companyId: string;
    companyName: string;
    readiness: OnboardingReadinessResult;
  } | null>(null);
  const [forceReason, setForceReason] = useState('');

  // Collapsible companies & entity groups
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set());
  const [collapsedEntities, setCollapsedEntities] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState<string | null>(null);

  // Template & office state
  const [templates, setTemplates] = useState<FileTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Record<string, string>>({});
  const [selectedOffice, setSelectedOffice] = useState<Record<string, string>>({});
  const [companyOffices, setCompanyOffices] = useState<Record<string, CompanyOffice[]>>({});

  // Entity mapping state per company
  const [entityGroups, setEntityGroups] = useState<Record<string, EntityGroup[]>>({});
  const [entityMappings, setEntityMappings] = useState<Record<string, EntityMappingLocal[]>>({});
  const [creatingMapping, setCreatingMapping] = useState<string | null>(null); // entityCode being created

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ file: UploadedFile; companyId: string } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Per-entity verification state
  const [verifyingEntity, setVerifyingEntity] = useState<string | null>(null);
  const [entityVerificationResults, setEntityVerificationResults] = useState<
    Record<string, {
      status: 'MATCH' | 'MISMATCH' | 'KNOWN_VARIANCE' | 'MISSING' | 'PARSE_ERROR';
      uploads: Array<{
        uploadId: string;
        fileName: string;
        overallStatus: string;
        sourceRecordCount: number;
        bigQueryRecordCount: number;
        sourceTotalCents: number;
        bigQueryTotalCents: number;
      }>;
      totalSourceRecords: number;
      totalBqRecords: number;
      totalSourceCents: number;
      totalBqCents: number;
    }>
  >({});
  const [expandedVerificationEntity, setExpandedVerificationEntity] = useState<Set<string>>(new Set());

  // Pagination: track which entity groups are expanded (show all files)
  const FILES_PER_PAGE = 50;
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Multi-select for file management (all entity groups)
  const [selectedFiles, setSelectedFiles] = useState<Record<string, Set<string>>>({});
  const [moveTarget, setMoveTarget] = useState<Record<string, string>>({});
  const [movingFiles, setMovingFiles] = useState(false);

  // Bulk delete state
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{
    companyId: string;
    fileIds: string[];
    fileNames: string[];
  } | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Per-group sort
  const [groupSort, setGroupSort] = useState<Record<string, 'name-asc' | 'name-desc' | 'date-desc'>>({});

  // Filename search per company
  const [fileSearch, setFileSearch] = useState<Record<string, string>>({});

  // Inline entity setup form state
  const [entitySetup, setEntitySetup] = useState<
    Record<string, { officeName: string; templateId: string; officeId: string; useExisting: boolean }>
  >({});

  const fetchPendingCompanies = useCallback(async () => {
    try {
      setToast(null);

      // Fetch all templates for the assignment dropdown
      try {
        const templatesSnap = await getDocs(collection(db, 'fileTemplates'));
        const templateList: FileTemplate[] = templatesSnap.docs.map((d) => ({
          id: d.id,
          templateName: d.data().templateName || d.id,
        }));
        templateList.sort((a, b) => a.templateName.localeCompare(b.templateName));
        setTemplates(templateList);
      } catch (templateErr) {
        console.warn('[OnboardingApprovals] Failed to fetch templates:', templateErr);
      }

      // 1. Fetch all companies
      const companiesSnap = await getDocs(collection(db, 'companies'));

      const pending: PendingCompany[] = [];

      for (const companyDoc of companiesSnap.docs) {
        const companyData = companyDoc.data();

        // 2. Check subscription is in onboarding state
        const subRef = doc(db, 'companies', companyDoc.id, 'subscription', 'config');
        const subSnap = await getDoc(subRef);

        if (!subSnap.exists()) continue;

        const subData = subSnap.data();
        if (subData?.state !== 'onboarding') continue;

        // 3. Fetch upload intents
        const intentsQuery = query(
          collection(db, 'upload_intents'),
          where('companyId', '==', companyDoc.id)
        );

        let uploadedFiles: UploadedFile[] = [];
        try {
          const intentsSnap = await getDocs(intentsQuery);
          uploadedFiles = intentsSnap.docs
            .filter((d) => d.data().status !== 'deleted')
            .map((d) => {
              const data = d.data();
              return {
                id: d.id,
                fileName: data.fileName || '',
                originalFileName: data.originalFileName || '',
                displayName: data.displayName || data.originalFileName || data.fileName || '',
                status: data.status || 'unknown',
                storagePath: data.storagePath || '',
                createdAt: data.createdAt,
                entityCode: data.entityCode || null,
                subscriberLoanBookName: data.subscriberLoanBookName || null,
                subscriberOfficeId: data.subscriberOfficeId || null,
                subscriberOfficeName: data.subscriberOfficeName || null,
                loanBookGroupKey: data.loanBookGroupKey || null,
              };
            })
            .sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
              const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
              return bTime - aTime;
            });
        } catch (intentErr) {
          console.warn(`[OnboardingApprovals] Failed to fetch intents for ${companyDoc.id}:`, intentErr);
        }

        const hasStagedFiles = uploadedFiles.some((f) => f.status === 'staged');

        // Fetch offices
        const officesSnap = await getDocs(collection(db, 'companies', companyDoc.id, 'offices'));
        const offices: CompanyOffice[] = officesSnap.docs.map((d) => ({
          id: d.id,
          name: d.data().name || 'Unnamed Office',
          isHeadOffice: d.data().isHeadOffice || false,
        }));
        setCompanyOffices((prev) => ({ ...prev, [companyDoc.id]: offices }));

        // Auto-select head office
        const headOffice = offices.find((o) => o.isHeadOffice) || offices[0];
        if (headOffice && !selectedOffice[companyDoc.id]) {
          setSelectedOffice((prev) => ({ ...prev, [companyDoc.id]: headOffice.id }));
        }

        // Fetch entity mappings for this company
        const mappingsSnap = await getDocs(
          collection(db, 'companies', companyDoc.id, 'entity_mappings')
        );
        const companyMappings: EntityMappingLocal[] = mappingsSnap.docs.map((d) => {
          const data = d.data();
          return {
            entityCode: d.id,
            officeId: data.officeId || '',
            officeName: data.officeName || '',
            templateId: data.templateId || '',
          };
        });
        setEntityMappings((prev) => ({ ...prev, [companyDoc.id]: companyMappings }));

        // Build entity groups from uploaded files
        const groups = groupFilesByEntityCode(uploadedFiles);
        // Mark groups as mapped if entity_mapping exists
        for (const group of groups) {
          if (group.entityCode === 'Unmatched') continue;
          const mapping = companyMappings.find((m) => m.entityCode === group.entityCode);
          if (mapping) {
            group.isMapped = true;
            group.mappedOfficeName = mapping.officeName;
            group.mappedTemplateId = mapping.templateId;
            group.mappedOfficeId = mapping.officeId;
          }
        }
        setEntityGroups((prev) => ({ ...prev, [companyDoc.id]: groups }));

        // Fetch aggregator preferences
        let aggregatorPreferences: string[] = [];
        try {
          const prefsSnap = await getDoc(doc(db, 'companies', companyDoc.id, 'preferences', 'aggregators'));
          if (prefsSnap.exists()) {
            aggregatorPreferences = prefsSnap.data()?.selected || [];
          }
        } catch {
          // Non-fatal
        }

        // Get creator info
        const creatorId = companyData.createdBy;
        let contactEmail = '';
        let contactName = '';
        if (creatorId) {
          try {
            const userSnap = await getDoc(doc(db, 'users', creatorId));
            if (userSnap.exists()) {
              contactEmail = userSnap.data()?.email || '';
              contactName = userSnap.data()?.displayName || '';
            }
          } catch {
            // Non-fatal
          }
        }

        pending.push({
          id: companyDoc.id,
          name: companyData.name || companyData.companyName || 'Unknown Company',
          aclNumber: companyData.acl || companyData.aclNumber,
          abn: companyData.abn,
          createdAt: companyData.createdAt,
          subscriptionTier: subData.tier,
          subscriptionAmount: subData.billing?.amount,
          dataProcessed: companyData.onboarding?.dataProcessed === true,
          dataProcessedAt: companyData.onboarding?.dataProcessedAt,
          uploadedFiles,
          commissionEmails: companyData.commissionEmails || [],
          otherAggregatorName: companyData.otherAggregatorName || null,
          enabledTemplateIds: companyData.enabledTemplateIds || [],
          aggregatorPreferences,
          hasStagedFiles,
          contactEmail,
          contactName,
        });
      }

      // Sort: data-processed first, then by creation date
      pending.sort((a, b) => {
        if (a.dataProcessed && !b.dataProcessed) return -1;
        if (!a.dataProcessed && b.dataProcessed) return 1;
        return 0;
      });

      setCompanies(pending);
    } catch (err: any) {
      console.error('[OnboardingApprovals] Fetch failed:', err);
      setToast({ message: err.message || 'Failed to load pending companies', type: 'error' });
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchPendingCompanies();
      setLoading(false);
    };
    load();
  }, [fetchPendingCompanies]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPendingCompanies();
    setRefreshing(false);
  };

  const handleDownload = async (file: UploadedFile) => {
    if (!file.storagePath) {
      setToast({ message: 'No storage path available for this file', type: 'error' });
      return;
    }

    setDownloading(file.id);
    try {
      const getDownloadUrl = httpsCallable(functions, 'getOnboardingFileDownloadUrl');
      const result = await getDownloadUrl({ storagePath: file.storagePath });
      const { downloadUrl } = result.data as { downloadUrl: string };
      window.open(downloadUrl, '_blank');
    } catch (err: any) {
      const msg = err?.message || 'Download failed';
      setToast({ message: `Failed to download ${file.displayName}: ${msg}`, type: 'error' });
    } finally {
      setDownloading(null);
    }
  };

  /**
   * Delete a staged file — calls deleteStagedFile callable
   */
  const handleDeleteFile = async (companyId: string, file: UploadedFile) => {
    setDeleting(file.id);
    setToast(null);

    try {
      const deleteFileFn = httpsCallable(functions, 'deleteStagedFile');
      await deleteFileFn({ companyId, uploadIntentId: file.id });

      setToast({ message: `Deleted ${file.originalFileName || file.displayName}`, type: 'success' });
      setDeleteConfirm(null);

      // Remove file from local state immediately (avoid full refetch)
      setCompanies((prev) =>
        prev.map((c) => {
          if (c.id !== companyId) return c;
          const updatedFiles = c.uploadedFiles.filter((f) => f.id !== file.id);
          return {
            ...c,
            uploadedFiles: updatedFiles,
            hasStagedFiles: updatedFiles.some((f) => f.status === 'staged'),
          };
        })
      );

      // Update entity groups
      setEntityGroups((prev) => {
        const groups = prev[companyId] || [];
        const updated = groups
          .map((g) => ({
            ...g,
            files: g.files.filter((f) => f.id !== file.id),
          }))
          .filter((g) => g.files.length > 0);
        return { ...prev, [companyId]: updated };
      });

      // Clear from selection
      setSelectedFiles((prev) => {
        const current = new Set(prev[companyId] || []);
        current.delete(file.id);
        return { ...prev, [companyId]: current };
      });
    } catch (err: any) {
      const msg = err?.message || 'Delete failed';
      setToast({ message: `Failed to delete ${file.originalFileName || file.displayName}: ${msg}`, type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  /**
   * Toggle pagination expansion for an entity group
   */
  const toggleGroupExpansion = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  /**
   * Toggle selection of a file for a given company (works across all entity groups)
   */
  const toggleFileSelection = (companyId: string, fileId: string) => {
    setSelectedFiles((prev) => {
      const current = new Set(prev[companyId] || []);
      if (current.has(fileId)) {
        current.delete(fileId);
      } else {
        current.add(fileId);
      }
      return { ...prev, [companyId]: current };
    });
  };

  /**
   * Select or deselect all deletable files in a group
   */
  const toggleAllInGroup = (companyId: string, groupFiles: UploadedFile[]) => {
    const deletableFiles = groupFiles.filter((f) => DELETABLE_FILE_STATUSES.includes(f.status));
    setSelectedFiles((prev) => {
      const current = new Set(prev[companyId] || []);
      const allSelected = deletableFiles.every((f) => current.has(f.id));
      if (allSelected) {
        deletableFiles.forEach((f) => current.delete(f.id));
      } else {
        deletableFiles.forEach((f) => current.add(f.id));
      }
      return { ...prev, [companyId]: new Set(current) };
    });
  };

  /**
   * Select all duplicate files in a group (keeps newest copy, selects the rest)
   */
  const selectDuplicates = (companyId: string, groupFiles: UploadedFile[], duplicateNames: Set<string>) => {
    const byName = new Map<string, UploadedFile[]>();
    for (const f of groupFiles) {
      if (!DELETABLE_FILE_STATUSES.includes(f.status)) continue;
      const name = f.originalFileName || f.fileName;
      if (duplicateNames.has(name)) {
        byName.set(name, [...(byName.get(name) || []), f]);
      }
    }
    const toSelect: string[] = [];
    byName.forEach((dupes) => {
      const sorted = [...dupes].sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime; // newest first
      });
      // Keep the newest (index 0), select the rest
      for (let i = 1; i < sorted.length; i++) toSelect.push(sorted[i].id);
    });
    setSelectedFiles((prev) => {
      const current = new Set(prev[companyId] || []);
      toSelect.forEach((id) => current.add(id));
      return { ...prev, [companyId]: new Set(current) };
    });
  };

  /**
   * Count selected files within a specific group
   */
  const getSelectedInGroup = (companyId: string, groupFiles: UploadedFile[]): number => {
    const selected = selectedFiles[companyId];
    if (!selected || selected.size === 0) return 0;
    return groupFiles.filter((f) => selected.has(f.id)).length;
  };

  /**
   * Open bulk delete confirmation
   */
  const handleBulkDelete = (companyId: string) => {
    const ids = Array.from(selectedFiles[companyId] || new Set());
    if (ids.length === 0) return;

    // Gather filenames for display
    const allFiles = (entityGroups[companyId] || []).flatMap((g) => g.files);
    const selectedFilesList = allFiles.filter((f) => ids.includes(f.id));
    const fileNames = selectedFilesList.map((f) => f.originalFileName || f.displayName);

    setBulkDeleteConfirm({ companyId, fileIds: ids, fileNames });
  };

  /**
   * Execute bulk delete — chunks into batches of 200
   */
  const confirmBulkDelete = async () => {
    if (!bulkDeleteConfirm) return;
    const { companyId, fileIds } = bulkDeleteConfirm;

    setBulkDeleting(true);
    setToast(null);

    try {
      const batchDeleteFn = httpsCallable(functions, 'batchDeleteStagedFiles');
      const BATCH_SIZE = 200;
      let totalDeleted = 0;
      const allErrors: string[] = [];

      for (let i = 0; i < fileIds.length; i += BATCH_SIZE) {
        const chunk = fileIds.slice(i, i + BATCH_SIZE);
        const result = await batchDeleteFn({ companyId, uploadIntentIds: chunk });
        const data = result.data as { deleted: number; errors: string[] };
        totalDeleted += data.deleted;
        if (data.errors.length > 0) allErrors.push(...data.errors);
      }

      // Remove deleted files from local state
      const deletedSet = new Set(fileIds);
      setCompanies((prev) =>
        prev.map((c) => {
          if (c.id !== companyId) return c;
          const updatedFiles = c.uploadedFiles.filter((f) => !deletedSet.has(f.id));
          return {
            ...c,
            uploadedFiles: updatedFiles,
            hasStagedFiles: updatedFiles.some((f) => f.status === 'staged'),
          };
        })
      );

      setEntityGroups((prev) => {
        const groups = prev[companyId] || [];
        const updated = groups
          .map((g) => ({
            ...g,
            files: g.files.filter((f) => !deletedSet.has(f.id)),
          }))
          .filter((g) => g.files.length > 0);
        return { ...prev, [companyId]: updated };
      });

      // Clear selection
      setSelectedFiles((prev) => ({ ...prev, [companyId]: new Set<string>() }));

      setToast({ message: `Deleted ${totalDeleted} file(s)` +
          (allErrors.length > 0 ? `. ${allErrors.length} skipped.` : ''), type: 'success' });
      setBulkDeleteConfirm(null);
    } catch (err: any) {
      const msg = err?.message || 'Bulk delete failed';
      setToast({ message: `Bulk delete failed: ${msg}`, type: 'error' });
    } finally {
      setBulkDeleting(false);
    }
  };

  /**
   * Move selected unmatched files to a target entity group.
   * Updates Firestore upload_intent docs and local state optimistically.
   */
  const handleMoveFiles = async (companyId: string, sourceEntityCode?: string) => {
    const targetKey = moveTarget[companyId];
    if (!targetKey) {
      setToast({ message: 'Please select a target entity group.', type: 'error' });
      return;
    }

    // Move files from the specified source group (or all selected if no source)
    const groups = entityGroups[companyId] || [];
    const sourceGroup = sourceEntityCode
      ? groups.find((g) => g.entityCode === sourceEntityCode)
      : groups.find((g) => g.entityCode === 'Unmatched');
    const selected = selectedFiles[companyId];
    if (!selected || selected.size === 0 || !sourceGroup) {
      setToast({ message: 'No files selected.', type: 'error' });
      return;
    }

    // Filter to only source group file IDs that are selected
    const sourceSelectedIds = sourceGroup.files
      .filter((f) => selected.has(f.id))
      .map((f) => f.id);

    if (sourceSelectedIds.length === 0) {
      setToast({ message: 'No files selected in this group to move.', type: 'error' });
      return;
    }

    // Find the target group to get its entity code and metadata
    const targetGroup = groups.find((g) => g.loanBookGroupKey === targetKey);
    if (!targetGroup) {
      setToast({ message: 'Target entity group not found.', type: 'error' });
      return;
    }

    setMovingFiles(true);
    setToast(null);

    // Optimistic local state update
    const fileIds = sourceSelectedIds;
    setEntityGroups((prev) => {
      const currentGroups = [...(prev[companyId] || [])];
      const sourceIdx = currentGroups.findIndex((g) => g.entityCode === (sourceEntityCode || 'Unmatched'));
      const targetIdx = currentGroups.findIndex((g) => g.loanBookGroupKey === targetKey);

      if (sourceIdx === -1 || targetIdx === -1) return prev;

      const src = { ...currentGroups[sourceIdx] };
      const tgt = { ...currentGroups[targetIdx] };

      const movedFiles = src.files.filter((f) => fileIds.includes(f.id));
      src.files = src.files.filter((f) => !fileIds.includes(f.id));
      tgt.files = [...tgt.files, ...movedFiles];

      const updated = [...currentGroups];
      updated[sourceIdx] = src;
      updated[targetIdx] = tgt;

      // Remove source group if empty
      return {
        ...prev,
        [companyId]: updated.filter((g) => g.files.length > 0),
      };
    });

    // Clear selection and target
    setSelectedFiles((prev) => {
      const current = new Set(prev[companyId] || []);
      for (const id of fileIds) current.delete(id);
      return { ...prev, [companyId]: new Set(current) };
    });
    setMoveTarget((prev) => ({ ...prev, [companyId]: '' }));

    // Persist to Firestore via callable (upload_intents write is admin-only)
    try {
      const reassignFn = httpsCallable(functions, 'reassignUploadIntents');
      await reassignFn({
        companyId,
        uploadIntentIds: fileIds,
        entityCode: targetGroup.entityCode,
        loanBookGroupKey: targetGroup.loanBookGroupKey,
        subscriberLoanBookName: targetGroup.subscriberName || null,
      });
      setToast({ message: `Moved ${fileIds.length} file(s) to ${targetGroup.subscriberName || targetGroup.entityCode}`, type: 'success' });
    } catch (err: any) {
      // Revert: refetch on error
      const msg = err?.message || 'Move failed';
      setToast({ message: `Failed to move files: ${msg}. Refreshing...`, type: 'error' });
      await fetchPendingCompanies();
    } finally {
      setMovingFiles(false);
    }
  };

  /**
   * Create office + entity mapping for an unmapped entity code
   */
  const handleCreateEntityMapping = async (companyId: string, entityCode: string, loanBookName?: string | null) => {
    const setup = entitySetup[`${companyId}_${entityCode}`];
    if (!setup) return;

    const { officeName, templateId, officeId, useExisting } = setup;
    if (!templateId) {
      setToast({ message: 'Please select a template for this entity.', type: 'error' });
      return;
    }
    if (useExisting && !officeId) {
      setToast({ message: 'Please select an office for this entity.', type: 'error' });
      return;
    }
    if (!useExisting && !officeName.trim()) {
      setToast({ message: 'Please enter an office name.', type: 'error' });
      return;
    }

    setCreatingMapping(`${companyId}_${entityCode}`);
    setToast(null);

    try {
      let resolvedOfficeId = officeId;
      let resolvedOfficeName = officeName.trim();

      // Create new office if not using existing
      if (!useExisting) {
        const officeRef = await addDoc(collection(db, 'companies', companyId, 'offices'), {
          name: resolvedOfficeName,
          isHeadOffice: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        resolvedOfficeId = officeRef.id;

        // Update local offices list
        setCompanyOffices((prev) => ({
          ...prev,
          [companyId]: [
            ...(prev[companyId] || []),
            { id: resolvedOfficeId, name: resolvedOfficeName, isHeadOffice: false },
          ],
        }));
      } else {
        // Get office name from existing offices
        const office = (companyOffices[companyId] || []).find((o) => o.id === officeId);
        resolvedOfficeName = office?.name || officeName;
      }

      // Create entity mapping via callable
      const setMappingFn = httpsCallable(functions, 'setEntityMapping');
      await setMappingFn({
        companyId,
        entityCode,
        officeId: resolvedOfficeId,
        officeName: resolvedOfficeName,
        legalEntityName: loanBookName || entityCode,
        templateId,
        isHeadOffice: false,
      });

      // Update local state
      setEntityMappings((prev) => ({
        ...prev,
        [companyId]: [
          ...(prev[companyId] || []),
          {
            entityCode,
            officeId: resolvedOfficeId,
            officeName: resolvedOfficeName,
            templateId,
          },
        ],
      }));

      // Mark group as mapped
      setEntityGroups((prev) => ({
        ...prev,
        [companyId]: (prev[companyId] || []).map((g) =>
          g.entityCode === entityCode
            ? {
                ...g,
                isMapped: true,
                mappedOfficeName: resolvedOfficeName,
                mappedTemplateId: templateId,
                mappedOfficeId: resolvedOfficeId,
              }
            : g
        ),
      }));

      setToast({ message: `Mapped ${entityCode} → ${resolvedOfficeName}`, type: 'success' });
    } catch (err: any) {
      const msg = err?.message || 'Failed to create mapping';
      setToast({ message: `Failed to map ${entityCode}: ${msg}`, type: 'error' });
      console.error('[OnboardingApprovals] Create mapping failed:', err);
    } finally {
      setCreatingMapping(null);
    }
  };

  /**
   * Process staged files — uses entity mapping mode when multiple entities detected
   */
  /**
   * Process a single entity group's staged files
   */
  const handleProcessSingleEntity = async (companyId: string, companyName: string, entityCode: string) => {
    // Ref-based guard: synchronous check prevents rapid double-clicks
    // (React setState is async — button may not disable before second click)
    const guardKey = `${companyId}_${entityCode}`;
    if (processingRef.current.has(guardKey)) return;
    processingRef.current.add(guardKey);
    setProcessing(companyId);
    setToast(null);
    try {
      // Use entity mapping as fallback for files with variant filenames
      // (e.g., "W4FI1.04" has loanBookGroupKey but no parseable entityCode)
      const mappings = entityMappings[companyId] || [];
      const mapping = mappings.find(m => m.entityCode === entityCode);

      const processFn = httpsCallable(functions, 'processOnboardingStagedFiles');
      const result = await processFn({
        companyId,
        templateId: mapping?.templateId || null,
        officeId: mapping?.officeId || null,
        useEntityMappings: true,
        entityCodes: [entityCode],
      });
      const data = result.data as any;
      if (data.remainingFiles > 0) {
        setToast({ message: `${companyName} — ${entityCode}: ${data.filesProcessed}/${data.totalFiles} files processed. ${data.remainingFiles} remaining — click Process again.`, type: 'success' });
      } else {
        setToast({ message: `${companyName} — ${entityCode}: ${data.filesProcessed} file(s) processed.`, type: 'success' });
      }
      await fetchPendingCompanies();
    } catch (err: any) {
      setToast({ message: `Failed to process ${entityCode}: ${err?.message || 'Unknown error'}`, type: 'error' });
    } finally {
      processingRef.current.delete(guardKey);
      setProcessing(null);
    }
  };

  /**
   * Verify a single entity group's processed files against BigQuery
   */
  const handleVerifyEntity = async (companyId: string, group: EntityGroup) => {
    const entityCode = group.entityCode;
    setVerifyingEntity(entityCode);
    setToast(null);

    try {
      // Step 1: List processed uploads for this entity
      const listFn = httpsCallable(functions, 'listCompanyUploads');
      const listResult = await listFn({
        companyId,
        templateId: group.mappedTemplateId,
        limit: 100,
      });
      const allUploads = (listResult.data as any).uploads || [];
      // Filter to this entity's uploads
      const entityUploads = allUploads.filter(
        (u: any) => u.entityCode === entityCode || (!u.entityCode && allUploads.length <= 1)
      );

      if (entityUploads.length === 0) {
        setEntityVerificationResults((prev) => ({
          ...prev,
          [entityCode]: {
            status: 'MISSING' as const,
            uploads: [],
            totalSourceRecords: 0,
            totalBqRecords: 0,
            totalSourceCents: 0,
            totalBqCents: 0,
          },
        }));
        return;
      }

      // Step 2: Verify each upload (sequentially to avoid overwhelming)
      const verifyFn = httpsCallable(functions, 'verifySourceAgainstBigQuery');
      const uploadResults: Array<{
        uploadId: string;
        fileName: string;
        overallStatus: string;
        sourceRecordCount: number;
        bigQueryRecordCount: number;
        sourceTotalCents: number;
        bigQueryTotalCents: number;
      }> = [];

      for (const upload of entityUploads) {
        try {
          const verifyResult = await verifyFn({
            companyId,
            templateId: upload.templateId,
            periodMonth: upload.periodMonth,
            runNumber: upload.runNumber,
            officeId: upload.officeId || group.mappedOfficeId,
            dataFilePath: upload.dataFilePath,
            rctiPdfPath: upload.rctiPdfPath,
            uploadId: upload.uploadId,
            isDecCombined: upload.isDecCombined,
          });
          const data = verifyResult.data as any;
          const dv = data.dataVerification;
          uploadResults.push({
            uploadId: upload.uploadId,
            fileName: upload.fileName,
            overallStatus: data.overallStatus || 'MISSING',
            sourceRecordCount: dv?.sourceRecordCount || 0,
            bigQueryRecordCount: dv?.bigQueryRecordCount || 0,
            sourceTotalCents: dv?.sourceTotals?.netCents || 0,
            bigQueryTotalCents: dv?.bigQueryTotals?.netCents || 0,
          });
        } catch (err: any) {
          uploadResults.push({
            uploadId: upload.uploadId,
            fileName: upload.fileName,
            overallStatus: 'PARSE_ERROR',
            sourceRecordCount: 0,
            bigQueryRecordCount: 0,
            sourceTotalCents: 0,
            bigQueryTotalCents: 0,
          });
        }
      }

      // Aggregate results
      const hasAnyMismatch = uploadResults.some((r) => r.overallStatus === 'MISMATCH');
      const hasAnyError = uploadResults.some((r) => r.overallStatus === 'PARSE_ERROR');
      const allMatch = uploadResults.every(
        (r) => r.overallStatus === 'MATCH' || r.overallStatus === 'KNOWN_VARIANCE'
      );

      setEntityVerificationResults((prev) => ({
        ...prev,
        [entityCode]: {
          status: hasAnyMismatch ? 'MISMATCH' : hasAnyError ? 'PARSE_ERROR' : allMatch ? 'MATCH' : 'KNOWN_VARIANCE',
          uploads: uploadResults,
          totalSourceRecords: uploadResults.reduce((s, r) => s + r.sourceRecordCount, 0),
          totalBqRecords: uploadResults.reduce((s, r) => s + r.bigQueryRecordCount, 0),
          totalSourceCents: uploadResults.reduce((s, r) => s + r.sourceTotalCents, 0),
          totalBqCents: uploadResults.reduce((s, r) => s + r.bigQueryTotalCents, 0),
        },
      }));
    } catch (err: any) {
      setToast({ message: `Verification failed for ${entityCode}: ${err?.message || 'Unknown error'}`, type: 'error' });
    } finally {
      setVerifyingEntity(null);
    }
  };

  /**
   * Process staged files — uses entity mapping mode when multiple entities detected
   */
  const handleProcessStaged = async (companyId: string, companyName: string) => {
    const groups = entityGroups[companyId] || [];
    const namedGroups = groups.filter((g) => g.entityCode !== 'Unmatched');
    const unmatchedGroup = groups.find((g) => g.entityCode === 'Unmatched');
    const isMultiEntity = namedGroups.length > 1;

    // Check if all named groups are mapped via entity mappings
    const allMapped = namedGroups.length > 0 && namedGroups.every((g) => g.isMapped);
    const useEntityMappingMode = isMultiEntity || allMapped;

    if (useEntityMappingMode) {
      // Entity mapping mode: check all entity codes are mapped
      const unmappedGroups = namedGroups.filter((g) => !g.isMapped);
      if (unmappedGroups.length > 0) {
        setToast({ message: `Please map all entity codes before processing: ${unmappedGroups.map((g) => g.entityCode).join(', ')}`, type: 'error' });
        return;
      }

      // If there are unmatched files, need a fallback template + office
      if (unmatchedGroup && unmatchedGroup.files.length > 0) {
        if (!selectedTemplate[companyId] || !selectedOffice[companyId]) {
          setToast({ message: 'Please select a fallback template and office for unmatched files.', type: 'error' });
          return;
        }
      }
    } else {
      // Single-entity mode (unmapped): require template + office
      if (!selectedTemplate[companyId]) {
        setToast({ message: 'Please select a template before processing.', type: 'error' });
        return;
      }
      if (!selectedOffice[companyId]) {
        setToast({ message: 'Please select an office before processing.', type: 'error' });
        return;
      }
    }

    // Ref-based guard: synchronous check prevents rapid double-clicks
    const guardKey = companyId;
    if (processingRef.current.has(guardKey)) return;
    processingRef.current.add(guardKey);
    setProcessing(companyId);
    setToast(null);
    setToast(null);

    try {
      const processFn = httpsCallable(functions, 'processOnboardingStagedFiles');
      const result = await processFn({
        companyId,
        templateId: selectedTemplate[companyId] || null,
        officeId: selectedOffice[companyId] || null,
        useEntityMappings: useEntityMappingMode,
      });
      const data = result.data as any;

      if (data.remainingFiles > 0) {
        setToast({ message: `${companyName}: ${data.filesProcessed}/${data.totalFiles} files processed. ${data.remainingFiles} remaining — click Process again.` +
            (data.errors?.length ? ` Warnings: ${data.errors.join('; ')}` : ''), type: 'success' });
      } else {
        setToast({ message: `${companyName}: ${data.filesProcessed} file(s) sent to processing pipeline.` +
            (data.errors?.length ? ` Warnings: ${data.errors.join('; ')}` : ''), type: 'success' });
      }

      await fetchPendingCompanies();
    } catch (err: any) {
      const msg = err?.message || 'Processing failed';
      setToast({ message: `Failed to process files for ${companyName}: ${msg}`, type: 'error' });
      console.error('[OnboardingApprovals] Process staged failed:', err);
    } finally {
      processingRef.current.delete(guardKey);
      setProcessing(null);
    }
  };

  /**
   * Two-step approval flow:
   *
   * 1. Run `verifyOnboardingReady` first. If it passes (8 oracles green),
   *    proceed straight to `approveOnboardingActivation` — no force needed.
   * 2. If it fails, open the gate modal showing CRITICAL oracle failures.
   *    The founder may either cancel OR enter a `forceActivationReason`
   *    (≥20 chars) and override. The reason is captured in the admin
   *    audit log via `approveOnboardingActivation`'s force-override branch.
   *
   * The harness call alone takes ~10-15s (8 oracles, 4 of which are
   * cross-codebase HTTPS callables). We surface a "Verifying readiness…"
   * toast so the founder sees progress.
   */
  const handleApprove = async (companyId: string, companyName: string) => {
    setApproving(companyId);
    setToast(null);

    try {
      // Step 1: dashboard-readiness pre-check.
      setToast({ message: `Verifying onboarding readiness for ${companyName}…`, type: 'success' });
      const verifyCallable = httpsCallable<{ companyId: string }, OnboardingReadinessResult>(
        functions,
        'verifyOnboardingReady'
      );
      const verifyResult = await verifyCallable({ companyId });
      const readiness = verifyResult.data;
      setToast(null);

      if (!readiness.passed) {
        // Open the force-with-reason modal — let the founder decide.
        setReadinessGate({ companyId, companyName, readiness });
        // setApproving stays set — the modal owns the next step. Cancel
        // path will reset it.
        return;
      }

      // Step 2: harness passed → activate immediately.
      const approve = httpsCallable<
        { companyId: string; force?: boolean; forceActivationReason?: string },
        { success: boolean }
      >(functions, 'approveOnboardingActivation');
      await approve({ companyId });
      setToast({ message: `${companyName} has been activated successfully.`, type: 'success' });
      setCompanies((prev) => prev.filter((c) => c.id !== companyId));
      setApproving(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Approval failed';
      setToast({ message: `Failed to approve ${companyName}: ${message}`, type: 'error' });
      setApproving(null);
    }
  };

  /**
   * Force-override path: invoked from the readiness gate modal after the
   * founder accepts the override warning and enters a reason.
   */
  const handleForceApprove = async (
    companyId: string,
    companyName: string,
    forceActivationReason: string
  ) => {
    if (forceActivationReason.trim().length < 20) {
      setToast({
        message: 'Reason must be at least 20 characters so the audit log explains why the override was used.',
        type: 'error',
      });
      return;
    }
    try {
      const approve = httpsCallable<
        { companyId: string; force: boolean; forceActivationReason: string },
        { success: boolean }
      >(functions, 'approveOnboardingActivation');
      await approve({ companyId, force: true, forceActivationReason: forceActivationReason.trim() });
      setToast({
        message: `${companyName} activated with override. Reason captured in admin audit log.`,
        type: 'success',
      });
      setCompanies((prev) => prev.filter((c) => c.id !== companyId));
      setReadinessGate(null);
      setApproving(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Force-approval failed';
      setToast({ message: `Failed to force-approve ${companyName}: ${message}`, type: 'error' });
    }
  };

  const handleCancelReadinessGate = () => {
    setReadinessGate(null);
    setApproving(null);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAmount = (cents: number | undefined): string => {
    if (!cents) return 'N/A';
    return `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0 })}`;
  };

  const getTemplateName = (templateId: string): string => {
    const tmpl = templates.find((t) => t.id === templateId);
    return tmpl?.templateName || templateId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Onboarding Approvals</h1>
          <p className="text-slate-400 text-sm mt-1">
            Review and activate self-service subscriber accounts
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Messages */}
      {/* Fixed-position toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Company list */}
      {companies.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <CheckCircle size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 text-lg">No pending approvals</p>
          <p className="text-slate-500 text-sm mt-1">
            All self-service subscribers have been reviewed.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {companies.map((company) => {
            const groups = entityGroups[company.id] || [];
            const namedGroups = groups.filter((g) => g.entityCode !== 'Unmatched');
            const unmatchedGroup = groups.find((g) => g.entityCode === 'Unmatched');
            const isMultiEntity = namedGroups.length > 1;
            const allMapped = namedGroups.every((g) => g.isMapped);
            const hasUnmatchedStagedFiles =
              unmatchedGroup?.files.some((f) => f.status === 'staged') || false;

            return (
              <div
                key={company.id}
                className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
              >
                {/* Company header — clickable to collapse */}
                <div
                  className="flex items-start justify-between cursor-pointer select-none"
                  onClick={() => setCollapsedCompanies((prev) => {
                    const next = new Set(prev);
                    next.has(company.id) ? next.delete(company.id) : next.add(company.id);
                    return next;
                  })}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      {collapsedCompanies.has(company.id) ? (
                        <ChevronRight size={20} className="text-blue-400" />
                      ) : (
                        <ChevronDown size={20} className="text-blue-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{company.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                        {company.aclNumber && <span>ACL: {company.aclNumber}</span>}
                        {company.abn && <span>ABN: {company.abn}</span>}
                        {company.subscriptionTier && (
                          <span className="capitalize">
                            {company.subscriptionTier} — {formatAmount(company.subscriptionAmount)}/mo
                          </span>
                        )}
                      </div>
                      {collapsedCompanies.has(company.id) ? (
                        <p className="text-xs text-slate-500 mt-1">
                          {company.uploadedFiles.length} files{namedGroups.length > 0 ? `, ${namedGroups.length} entit${namedGroups.length !== 1 ? 'ies' : 'y'}` : ''}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500 mt-1">
                          Signed up: {formatDate(company.createdAt)}
                        </p>
                      )}
                    </div>
                  </div>
                  {company.dataProcessed ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400 bg-green-500/10 px-2.5 py-1 rounded-full">
                      <CheckCircle size={12} />
                      Data Processed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-full">
                      <Clock size={12} />
                      Awaiting Data
                    </span>
                  )}
                </div>

                {!collapsedCompanies.has(company.id) && (<>
                {/* Subscriber Details */}
                <div className="mt-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-400">
                  {company.contactName && (
                    <div>Contact: <span className="text-slate-300">{company.contactName}</span></div>
                  )}
                  {company.contactEmail && (
                    <div>Email: <span className="text-slate-300">{company.contactEmail}</span></div>
                  )}
                  {company.aggregatorPreferences && company.aggregatorPreferences.length > 0 && (
                    <div>Aggregators: <span className="text-slate-300">{company.aggregatorPreferences.join(', ')}</span></div>
                  )}
                  {company.otherAggregatorName && (
                    <div className="text-amber-400">Other Aggregator: <span className="font-medium">{company.otherAggregatorName}</span></div>
                  )}
                  {company.commissionEmails && company.commissionEmails.length > 0 && (
                    <div className="col-span-2 flex items-center gap-1">
                      <Mail size={12} className="text-cyan-400" />
                      Commission emails: <span className="text-slate-300">{company.commissionEmails.join(', ')}</span>
                    </div>
                  )}
                  <div>
                    Templates: <span className={company.enabledTemplateIds && company.enabledTemplateIds.length > 0 ? 'text-green-400' : 'text-amber-400'}>
                      {company.enabledTemplateIds && company.enabledTemplateIds.length > 0
                        ? `${company.enabledTemplateIds.length} assigned`
                        : 'None assigned'}
                    </span>
                  </div>
                </div>

                {/* Entity-grouped files */}
                {company.uploadedFiles.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                        <Layers size={12} />
                        Uploaded Files ({company.uploadedFiles.length})
                        {isMultiEntity && (
                          <span className="ml-1 text-blue-400">
                            — {namedGroups.length} entity groups detected
                          </span>
                        )}
                        {company.hasStagedFiles && !isMultiEntity && !namedGroups.every((g) => g.isMapped) && (
                          <span className="ml-1 text-amber-400">
                            — submitted files need template assignment
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                          <input
                            type="text"
                            value={fileSearch[company.id] || ''}
                            onChange={(e) => setFileSearch((prev) => ({ ...prev, [company.id]: e.target.value }))}
                            placeholder="Filter files..."
                            className="pl-7 pr-2 py-1 bg-slate-800 border border-slate-600 rounded text-xs text-white placeholder-slate-500 w-40 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Company-level bulk action bar */}
                    {(selectedFiles[company.id]?.size || 0) > 0 && (
                      <div className="flex items-center gap-3 mb-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <span className="text-xs text-blue-300 font-medium">
                          {selectedFiles[company.id]!.size} file{selectedFiles[company.id]!.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                          onClick={() => handleBulkDelete(company.id)}
                          disabled={bulkDeleting}
                          className="px-2.5 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                        >
                          <Trash2 size={11} />
                          Delete Selected
                        </button>
                        <button
                          onClick={() => setSelectedFiles((prev) => ({ ...prev, [company.id]: new Set<string>() }))}
                          className="text-xs text-slate-400 hover:text-white transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    <div className="space-y-3">
                      {groups.map((group) => {
                        const setupKey = `${company.id}_${group.entityCode}`;
                        const setup = entitySetup[setupKey] || {
                          officeName: group.subscriberName || group.entityCode,
                          templateId: '',
                          officeId: '',
                          useExisting: false,
                        };

                        return (
                          <div
                            key={group.entityCode}
                            className="border border-slate-700 rounded-lg overflow-hidden"
                          >
                            {/* Group header — clickable to collapse */}
                            <div
                              className="bg-slate-900/60 px-4 py-2 border-b border-slate-700 flex items-center justify-between cursor-pointer select-none"
                              onClick={() => {
                                const entityKey = `${company.id}_${group.entityCode}`;
                                setCollapsedEntities((prev) => {
                                  const next = new Set(prev);
                                  next.has(entityKey) ? next.delete(entityKey) : next.add(entityKey);
                                  return next;
                                });
                              }}
                            >
                              <div className="flex items-center gap-2">
                                {collapsedEntities.has(`${company.id}_${group.entityCode}`) ? (
                                  <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
                                ) : (
                                  <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
                                )}
                                <span className="font-medium text-slate-200">{group.entityCode}</span>
                                <span className="text-xs text-slate-500">({group.files.length} files)</span>
                                {group.subscriberName && group.subscriberName !== group.entityCode && (
                                  <span className="text-xs text-blue-400 italic">
                                    — "{group.subscriberName}"
                                  </span>
                                )}
                              </div>
                              {group.entityCode !== 'Unmatched' && (
                                group.isMapped ? (
                                  <span className="text-xs text-green-400 flex items-center gap-1">
                                    <CheckCircle size={12} />
                                    {group.mappedOfficeName}
                                    {group.mappedTemplateId && (
                                      <span className="text-slate-500 ml-1">
                                        ({getTemplateName(group.mappedTemplateId)})
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Re-open assignment form by unmarking as mapped
                                        setEntityGroups((prev) => ({
                                          ...prev,
                                          [company.id]: (prev[company.id] || []).map((g) =>
                                            g.entityCode === group.entityCode
                                              ? { ...g, isMapped: false }
                                              : g
                                          ),
                                        }));
                                      }}
                                      className="ml-2 text-slate-400 hover:text-blue-400 transition-colors"
                                      title="Edit mapping"
                                    >
                                      Edit
                                    </button>
                                    {group.files.some(f => f.status === 'staged') && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleProcessSingleEntity(company.id, company.name, group.entityCode); }}
                                        disabled={processing === company.id}
                                        className="ml-2 px-2 py-0.5 bg-cyan-600 text-white rounded text-xs hover:bg-cyan-700 transition-colors disabled:opacity-50"
                                      >
                                        Process
                                      </button>
                                    )}
                                    {group.isMapped && group.files.some(f => f.status !== 'staged' && f.status !== 'deleted') && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleVerifyEntity(company.id, group); }}
                                        disabled={verifyingEntity === group.entityCode}
                                        className="ml-2 px-2 py-0.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                      >
                                        {verifyingEntity === group.entityCode ? (
                                          <><Loader2 size={10} className="animate-spin" /> Verifying...</>
                                        ) : (
                                          <><ShieldCheck size={10} /> Verify</>
                                        )}
                                      </button>
                                    )}
                                    {entityVerificationResults[group.entityCode] && (
                                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        entityVerificationResults[group.entityCode].status === 'MATCH'
                                          ? 'bg-green-500/20 text-green-400'
                                          : entityVerificationResults[group.entityCode].status === 'KNOWN_VARIANCE'
                                          ? 'bg-blue-500/20 text-blue-400'
                                          : entityVerificationResults[group.entityCode].status === 'MISMATCH'
                                          ? 'bg-red-500/20 text-red-400'
                                          : 'bg-orange-500/20 text-orange-400'
                                      }`}>
                                        {entityVerificationResults[group.entityCode].status}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-xs text-amber-400 flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    Not mapped
                                  </span>
                                )
                              )}
                            </div>

                            {!collapsedEntities.has(`${company.id}_${group.entityCode}`) && (<>
                            {/* Per-group action bar — select all, sort, duplicates, move (Unmatched only) */}
                            {(() => {
                              const deletableInGroup = group.files.filter((f) => DELETABLE_FILE_STATUSES.includes(f.status));
                              const selectedInGroup = getSelectedInGroup(company.id, group.files);
                              const groupKey = `${company.id}_${group.entityCode}`;
                              const currentSort = groupSort[groupKey] || 'date-desc';
                              const duplicateNames = detectDuplicates(group.files);
                              const dupCount = duplicateNames.size > 0 ? countDuplicateCopies(group.files, duplicateNames) : 0;
                              const allInGroupSelected = deletableInGroup.length > 0 && deletableInGroup.every((f) => (selectedFiles[company.id] || new Set()).has(f.id));

                              if (deletableInGroup.length === 0 && duplicateNames.size === 0) return null;

                              return (
                                <div className="bg-slate-900/40 border-b border-slate-700 px-4 py-2 space-y-2">
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-3">
                                      {deletableInGroup.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => toggleAllInGroup(company.id, group.files)}
                                          className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1.5 transition-colors"
                                        >
                                          {allInGroupSelected ? (
                                            <><CheckSquare size={14} /> Deselect All</>
                                          ) : (
                                            <><Square size={14} /> Select All</>
                                          )}
                                        </button>
                                      )}
                                      {dupCount > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => selectDuplicates(company.id, group.files, duplicateNames)}
                                          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1.5 transition-colors"
                                        >
                                          <CopyMinus size={14} />
                                          Select {dupCount} duplicate{dupCount !== 1 ? 's' : ''}
                                        </button>
                                      )}
                                      {selectedInGroup > 0 && (
                                        <>
                                        <span className="text-xs text-slate-400">
                                          {selectedInGroup} selected
                                        </span>
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); handleBulkDelete(company.id); }}
                                          disabled={bulkDeleting}
                                          className="px-2.5 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                        >
                                          <Trash2 size={11} />
                                          Delete Selected
                                        </button>
                                        </>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const next = currentSort === 'date-desc' ? 'name-asc' : currentSort === 'name-asc' ? 'name-desc' : 'date-desc';
                                          setGroupSort((prev) => ({ ...prev, [groupKey]: next }));
                                        }}
                                        className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                                        title="Toggle sort order"
                                      >
                                        <ArrowUpDown size={12} />
                                        {currentSort === 'name-asc' ? 'A-Z' : currentSort === 'name-desc' ? 'Z-A' : 'Newest'}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Move-to action — available on ALL groups when files are selected */}
                                  {groups.length > 1 && selectedInGroup > 0 && (
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs text-slate-400 whitespace-nowrap">Move to:</label>
                                      <select
                                        value={moveTarget[company.id] || ''}
                                        onChange={(e) =>
                                          setMoveTarget((prev) => ({ ...prev, [company.id]: e.target.value }))
                                        }
                                        className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-white"
                                      >
                                        <option value="">Select entity group...</option>
                                        {groups
                                          .filter((g) => g.entityCode !== group.entityCode)
                                          .map((g) => (
                                            <option key={g.loanBookGroupKey} value={g.loanBookGroupKey}>
                                              {g.entityCode}
                                              {g.subscriberName && g.subscriberName !== g.entityCode
                                                ? ` — ${g.subscriberName}`
                                                : ''}
                                              {g.isMapped && g.mappedOfficeName
                                                ? ` (${g.mappedOfficeName})`
                                                : ''}
                                            </option>
                                          ))}
                                      </select>
                                      <button
                                        onClick={() => handleMoveFiles(company.id, group.entityCode)}
                                        disabled={movingFiles || !moveTarget[company.id]}
                                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                                      >
                                        {movingFiles ? (
                                          <><Loader2 size={12} className="animate-spin" /> Moving...</>
                                        ) : (
                                          <><ArrowRight size={12} /> Move</>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Inline entity setup widget for unmapped groups */}
                            {group.entityCode !== 'Unmatched' &&
                              !group.isMapped &&
                              group.files.some((f) => f.status === 'staged') && (
                                <div className="bg-amber-900/10 border-b border-amber-500/20 px-4 py-3">
                                  <p className="text-xs text-amber-400 mb-2 flex items-center gap-1">
                                    <Tag size={11} />
                                    Assign office + template for {group.entityCode}
                                  </p>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                    {/* Office: existing or new */}
                                    <div>
                                      <label className="block text-[11px] text-slate-500 mb-0.5">
                                        Office
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setEntitySetup((prev) => ({
                                              ...prev,
                                              [setupKey]: { ...setup, useExisting: !setup.useExisting },
                                            }))
                                          }
                                          className="ml-2 text-blue-400 hover:text-blue-300"
                                        >
                                          ({setup.useExisting ? 'create new' : 'use existing'})
                                        </button>
                                      </label>
                                      {setup.useExisting ? (
                                        <select
                                          value={setup.officeId}
                                          onChange={(e) =>
                                            setEntitySetup((prev) => ({
                                              ...prev,
                                              [setupKey]: { ...setup, officeId: e.target.value },
                                            }))
                                          }
                                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-white"
                                        >
                                          <option value="">Select office...</option>
                                          {(companyOffices[company.id] || []).map((o) => (
                                            <option key={o.id} value={o.id}>
                                              {o.name}{o.isHeadOffice ? ' (HQ)' : ''}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={setup.officeName}
                                          onChange={(e) =>
                                            setEntitySetup((prev) => ({
                                              ...prev,
                                              [setupKey]: { ...setup, officeName: e.target.value },
                                            }))
                                          }
                                          placeholder="Office name..."
                                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-white placeholder-slate-500"
                                        />
                                      )}
                                    </div>

                                    {/* Template */}
                                    <div>
                                      <label className="block text-[11px] text-slate-500 mb-0.5">Template</label>
                                      <select
                                        value={setup.templateId}
                                        onChange={(e) =>
                                          setEntitySetup((prev) => ({
                                            ...prev,
                                            [setupKey]: { ...setup, templateId: e.target.value },
                                          }))
                                        }
                                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-white"
                                      >
                                        <option value="">Select template...</option>
                                        {templates.map((t) => (
                                          <option key={t.id} value={t.id}>
                                            {t.templateName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {/* Create & Map button */}
                                    <div className="flex items-end">
                                      <button
                                        onClick={() => handleCreateEntityMapping(company.id, group.entityCode, group.subscriberName)}
                                        disabled={creatingMapping === setupKey}
                                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                      >
                                        {creatingMapping === setupKey ? (
                                          <>
                                            <Loader2 size={12} className="animate-spin" />
                                            Saving...
                                          </>
                                        ) : (
                                          <>
                                            <CheckCircle size={12} />
                                            Assign
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* File list with pagination, sort, filter, duplicate detection */}
                            <div className="divide-y divide-slate-700/50">
                              {(() => {
                                const groupKey = `${company.id}_${group.entityCode}`;
                                const isExpanded = expandedGroups.has(groupKey);
                                const currentSort = groupSort[groupKey] || 'date-desc';
                                const searchTerm = (fileSearch[company.id] || '').toLowerCase().trim();
                                const duplicateNames = detectDuplicates(group.files);

                                // Sort → Filter → Paginate
                                let sortedFiles = applySortToFiles(group.files, currentSort);
                                if (searchTerm) {
                                  sortedFiles = sortedFiles.filter((f) =>
                                    (f.originalFileName || f.displayName || '').toLowerCase().includes(searchTerm)
                                  );
                                }
                                const visibleFiles = isExpanded
                                  ? sortedFiles
                                  : sortedFiles.slice(0, FILES_PER_PAGE);
                                const hiddenCount = sortedFiles.length - FILES_PER_PAGE;

                                if (searchTerm && sortedFiles.length === 0) {
                                  return (
                                    <p className="text-xs text-slate-500 px-4 py-3 italic">No files match "{searchTerm}"</p>
                                  );
                                }

                                return (
                                  <>
                                    {visibleFiles.map((file) => {
                                      const isSelected = (selectedFiles[company.id] || new Set()).has(file.id);
                                      const isDeletable = DELETABLE_FILE_STATUSES.includes(file.status);
                                      const isDuplicate = duplicateNames.has(file.originalFileName || file.fileName);

                                      return (
                                        <div
                                          key={file.id}
                                          onClick={() => isDeletable && toggleFileSelection(company.id, file.id)}
                                          className={`flex items-center gap-2 text-sm px-4 py-1.5 transition-colors group ${
                                            isDeletable ? 'cursor-pointer' : ''
                                          } ${
                                            isSelected
                                              ? 'bg-blue-500/10 border-l-2 border-l-blue-500'
                                              : 'hover:bg-slate-800/50 border-l-2 border-l-transparent'
                                          }`}
                                        >
                                          {isDeletable && (
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); toggleFileSelection(company.id, file.id); }}
                                              className="flex-shrink-0 text-slate-400 hover:text-blue-400 transition-colors"
                                            >
                                              {isSelected ? (
                                                <CheckSquare size={14} className="text-blue-400" />
                                              ) : (
                                                <Square size={14} />
                                              )}
                                            </button>
                                          )}
                                          <FileText size={14} className="text-slate-500 flex-shrink-0" />
                                          <span className="text-slate-300 truncate flex-1">
                                            {file.originalFileName || file.displayName}
                                          </span>
                                          {isDuplicate && (
                                            <span className="text-[10px] text-amber-400 flex-shrink-0 px-1.5 py-0.5 bg-amber-500/10 rounded" title="Duplicate filename">
                                              duplicate
                                            </span>
                                          )}
                                          {file.storagePath && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                              disabled={downloading === file.id}
                                              title="Download file"
                                              className={`text-slate-400 hover:text-blue-400 transition-colors disabled:opacity-50 ${
                                                isSelected ? '' : 'opacity-0 group-hover:opacity-100'
                                              }`}
                                            >
                                              {downloading === file.id ? (
                                                <Loader2 size={14} className="animate-spin" />
                                              ) : (
                                                <Download size={14} />
                                              )}
                                            </button>
                                          )}
                                          {isDeletable && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ file, companyId: company.id }); }}
                                              disabled={deleting === file.id || bulkDeleting}
                                              title="Delete file"
                                              className={`text-slate-500 hover:text-red-400 transition-colors disabled:opacity-50 ${
                                                isSelected ? '' : 'opacity-0 group-hover:opacity-100'
                                              }`}
                                            >
                                              {deleting === file.id ? (
                                                <Loader2 size={14} className="animate-spin" />
                                              ) : (
                                                <Trash2 size={14} />
                                              )}
                                            </button>
                                          )}
                                          <span
                                            className={`text-xs flex-shrink-0 ${
                                              file.status === 'processed'
                                                ? 'text-green-400'
                                                : file.status === 'staged'
                                                  ? 'text-cyan-400'
                                                  : file.status === 'pending'
                                                    ? 'text-amber-400'
                                                    : file.status === 'deleted'
                                                      ? 'text-red-400'
                                                      : 'text-slate-500'
                                            }`}
                                          >
                                            {file.status === 'staged' ? 'Submitted' : file.status}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {hiddenCount > 0 && (
                                      <button
                                        onClick={() => toggleGroupExpansion(groupKey)}
                                        className="w-full text-center py-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-slate-900/30 transition-colors"
                                      >
                                        {isExpanded
                                          ? `Collapse to ${FILES_PER_PAGE} files`
                                          : `Show all ${sortedFiles.length} files (+${hiddenCount} more)`}
                                      </button>
                                    )}
                                  </>
                                );
                              })()}

                              {/* Inline verification results */}
                              {entityVerificationResults[group.entityCode] && (() => {
                                const vr = entityVerificationResults[group.entityCode];
                                const isExpVer = expandedVerificationEntity.has(group.entityCode);
                                return (
                                  <div className="mx-4 mb-3 p-3 bg-slate-900/40 rounded-lg border border-slate-700/30">
                                    <div
                                      className="flex items-center justify-between cursor-pointer"
                                      onClick={() => setExpandedVerificationEntity((prev) => {
                                        const next = new Set(prev);
                                        next.has(group.entityCode) ? next.delete(group.entityCode) : next.add(group.entityCode);
                                        return next;
                                      })}
                                    >
                                      <div className="flex items-center gap-2 text-xs">
                                        <ShieldCheck size={12} className={
                                          vr.status === 'MATCH' ? 'text-green-400' :
                                          vr.status === 'KNOWN_VARIANCE' ? 'text-blue-400' :
                                          vr.status === 'MISMATCH' ? 'text-red-400' : 'text-orange-400'
                                        } />
                                        <span className="text-slate-300 font-medium">Verification</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                          vr.status === 'MATCH' ? 'bg-green-500/20 text-green-400' :
                                          vr.status === 'KNOWN_VARIANCE' ? 'bg-blue-500/20 text-blue-400' :
                                          vr.status === 'MISMATCH' ? 'bg-red-500/20 text-red-400' :
                                          'bg-orange-500/20 text-orange-400'
                                        }`}>{vr.status}</span>
                                      </div>
                                      <div className="flex items-center gap-3 text-[10px] text-slate-400">
                                        <span>Records: {vr.totalSourceRecords} source / {vr.totalBqRecords} BQ</span>
                                        {isExpVer ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                      </div>
                                    </div>

                                    {isExpVer && (
                                      <div className="mt-2 space-y-1">
                                        <div className="grid grid-cols-3 gap-2 text-[10px] text-slate-400 font-medium border-b border-slate-700/30 pb-1">
                                          <div>Source Total</div>
                                          <div className="text-right">BigQuery Total</div>
                                          <div className="text-right">Difference</div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 text-xs">
                                          <div className="text-slate-300">
                                            ${(vr.totalSourceCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                                          </div>
                                          <div className="text-right text-slate-300">
                                            ${(vr.totalBqCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                                          </div>
                                          <div className={`text-right ${
                                            Math.abs(vr.totalSourceCents - vr.totalBqCents) > 100 ? 'text-red-400' :
                                            vr.totalSourceCents !== vr.totalBqCents ? 'text-yellow-400' : 'text-green-400'
                                          }`}>
                                            ${((vr.totalSourceCents - vr.totalBqCents) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                                          </div>
                                        </div>

                                        {vr.uploads.length > 1 && (
                                          <div className="mt-2 space-y-0.5">
                                            <div className="text-[10px] text-slate-500 font-medium">Per-file breakdown:</div>
                                            {vr.uploads.map((u) => (
                                              <div key={u.uploadId} className="flex items-center justify-between text-[10px] py-0.5">
                                                <span className="text-slate-400 truncate max-w-[200px]">{u.fileName}</span>
                                                <span className={
                                                  u.overallStatus === 'MATCH' ? 'text-green-400' :
                                                  u.overallStatus === 'KNOWN_VARIANCE' ? 'text-blue-400' :
                                                  u.overallStatus === 'MISMATCH' ? 'text-red-400' : 'text-orange-400'
                                                }>{u.overallStatus}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            </>)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {company.uploadedFiles.length === 0 && (
                  <p className="text-xs text-slate-500 mb-4">No files uploaded yet.</p>
                )}

                {/* Processing section — adapts based on mapping state */}
                {company.hasStagedFiles && (() => {
                  const allGroupsMapped = namedGroups.length > 0 && namedGroups.every((g) => g.isMapped);
                  const hasUnmatched = unmatchedGroup && unmatchedGroup.files.length > 0;
                  const needsFallback = !allGroupsMapped && namedGroups.length === 0;

                  return (
                    <div className="mb-4 space-y-3">
                      {/* Fallback template/office — only for unmapped single-entity or unmatched files */}
                      {(needsFallback || (hasUnmatched && allGroupsMapped)) && (
                        <div className="p-4 bg-cyan-900/20 border border-cyan-500/20 rounded-lg">
                          <p className="text-xs font-medium text-cyan-400 mb-3 flex items-center gap-1.5">
                            <Tag size={12} />
                            {hasUnmatched && allGroupsMapped
                              ? 'Fallback Template & Office (for unmatched files only)'
                              : 'Assign Template & Process Submitted Files'}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Template</label>
                              <select
                                value={selectedTemplate[company.id] || ''}
                                onChange={(e) => setSelectedTemplate((prev) => ({ ...prev, [company.id]: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              >
                                <option value="">Select a template...</option>
                                {templates.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.templateName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Office</label>
                              <select
                                value={selectedOffice[company.id] || ''}
                                onChange={(e) => setSelectedOffice((prev) => ({ ...prev, [company.id]: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              >
                                <option value="">Select an office...</option>
                                {(companyOffices[company.id] || []).map((o) => (
                                  <option key={o.id} value={o.id}>
                                    {o.name}{o.isHeadOffice ? ' (HQ)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Process button — always visible when there are staged files */}
                      <button
                        onClick={() => handleProcessStaged(company.id, company.name)}
                        disabled={
                          processing === company.id ||
                          creatingMapping !== null ||
                          (!allGroupsMapped && namedGroups.some((g) => !g.isMapped)) ||
                          (needsFallback && (!selectedTemplate[company.id] || !selectedOffice[company.id]))
                        }
                        className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {processing === company.id ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Play size={14} />
                            Process {namedGroups.length > 0 ? `${namedGroups.length} Loan Book${namedGroups.length !== 1 ? 's' : ''}` : 'Files'}
                          </>
                        )}
                      </button>
                      {namedGroups.some((g) => !g.isMapped) && (
                        <p className="text-xs text-amber-400">
                          Map all loan book entities above before processing.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-3 border-t border-slate-700">
                  <a
                    href={`/companies/${company.id}`}
                    className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg text-sm hover:bg-slate-600 transition-colors"
                  >
                    View Details
                  </a>
                  <button
                    onClick={() => handleApprove(company.id, company.name)}
                    disabled={approving === company.id || company.hasStagedFiles}
                    title={company.hasStagedFiles ? 'Process submitted files first' : undefined}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {approving === company.id ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} />
                        Approve &amp; Activate
                      </>
                    )}
                  </button>
                </div>
                </>)}
              </div>
            );
          })}
        </div>
      )}
      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Trash2 size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete File</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Delete <span className="text-slate-200 font-medium">{deleteConfirm.file.originalFileName || deleteConfirm.file.displayName}</span>? This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting !== null}
                className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg text-sm hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFile(deleteConfirm.companyId, deleteConfirm.file)}
                disabled={deleting !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk delete confirmation dialog */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Trash2 size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete {bulkDeleteConfirm.fileIds.length} Files</h3>
                <p className="text-sm text-slate-400 mt-1">
                  This will permanently delete {bulkDeleteConfirm.fileIds.length} file{bulkDeleteConfirm.fileIds.length !== 1 ? 's' : ''}. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="mb-4 max-h-40 overflow-y-auto bg-slate-900/50 rounded-lg p-3">
              {bulkDeleteConfirm.fileNames.slice(0, 10).map((name, i) => (
                <p key={i} className="text-xs text-slate-400 truncate">{name}</p>
              ))}
              {bulkDeleteConfirm.fileNames.length > 10 && (
                <p className="text-xs text-slate-500 mt-1">...and {bulkDeleteConfirm.fileNames.length - 10} more</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setBulkDeleteConfirm(null)}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg text-sm hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {bulkDeleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Delete {bulkDeleteConfirm.fileIds.length} Files
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding readiness gate modal — opens after a CRITICAL harness
          failure when the founder clicks Approve. Forces the founder to
          either cancel OR enter a ≥20-char reason and override. The reason
          is captured in admin_audit_logs by approveOnboardingActivation's
          force-override branch. */}
      {readinessGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-amber-600/50 rounded-xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="text-amber-400 flex-shrink-0 mt-1" size={28} />
              <div>
                <h2 className="text-xl font-bold text-white">
                  Onboarding readiness check failed
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  {readinessGate.companyName} — {readinessGate.readiness.diagnostic}
                </p>
              </div>
            </div>

            <div className="bg-slate-950/50 border border-slate-700 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-2">
                Failed oracles ({readinessGate.readiness.blockingFailures} blocking,
                {' '}{readinessGate.readiness.warnings} warning):
              </h3>
              <ul className="space-y-2">
                {[
                  ...readinessGate.readiness.loginReadiness.oracles,
                  ...readinessGate.readiness.dashboardReadiness.oracles,
                ]
                  .filter((o) => !o.passed)
                  .map((o) => (
                    <li key={o.name} className="flex items-start gap-2 text-sm">
                      {o.severity === 'CRITICAL' ? (
                        <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={14} />
                      ) : (
                        <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-0.5" size={14} />
                      )}
                      <div>
                        <code className="text-slate-200 font-mono text-xs">{o.name}</code>
                        {o.detail && (
                          <p className="text-slate-400 text-xs mt-0.5">{o.detail}</p>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="mb-4">
              <label htmlFor="forceReasonInput" className="block text-sm font-medium text-slate-200 mb-1">
                Force-activate reason (≥20 characters, required)
              </label>
              <textarea
                id="forceReasonInput"
                value={forceReason}
                onChange={(e) => setForceReason(e.target.value)}
                placeholder="Why is this override necessary? Captured in the admin audit log forever."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-amber-500"
                rows={3}
                aria-describedby="forceReasonHelp"
              />
              <p id="forceReasonHelp" className="text-xs text-slate-500 mt-1">
                {forceReason.trim().length}/20 characters minimum.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelReadinessGate}
                className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleForceApprove(
                    readinessGate.companyId,
                    readinessGate.companyName,
                    forceReason
                  );
                }}
                disabled={forceReason.trim().length < 20}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ShieldAlert size={14} />
                Force activate (override harness)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingApprovalsPage;
